import Section from '@/common/components/atom/section/section';
import { COLLAPSED_WHILE_PENDING, ProfileSectionSkeleton } from '@/modules/profile/components/ProfileSectionSkeleton';
import type { RevealState } from '@/modules/profile/utils/sectionReadiness';

type Props = {
  sections: readonly any[];
  /** main column renders `fallback` for `isShowFallback` sections; the aside does not */
  allowFallback: boolean;
  /** progressive only: reveal state per section position; omitted → today's rendering */
  states?: RevealState[];
  /**
   * progressive only: changes whenever any section on the page is revealed. The pending
   * skeletons are rendered inside one wrapper keyed by it, so each reveal mounts *fresh*
   * skeleton nodes instead of moving the old ones. Layout-shift (CLS) only counts existing
   * nodes that move; without this every hidden section collapsing at the reveal boundary
   * slid the skeletons below it through the viewport (measured CLS ≈ 1.0 on real profiles).
   */
  pendingKey?: string | number;
};

const renderSection = (section: any, index: number) =>
  section.noWrapper ? (
    // Section renders noWrapper children bare; `contents` keeps the layout unchanged.
    <div key={index} className="contents" data-section-id={section.sectionId}>
      <Section {...section}>{section[section.isShow ? 'children' : 'fallback']?.(section?.function?.())}</Section>
    </div>
  ) : (
    <Section key={index} {...section} dataAttributes={{ ...section.dataAttributes, 'data-section-id': section.sectionId }}>
      {section[section.isShow ? 'children' : 'fallback']?.(section?.function?.())}
    </Section>
  );

/**
 * Entries that usually render nothing on mobile reserve no space while pending (see
 * COLLAPSED_WHILE_PENDING): bare slots (`noWrapper`: Hamdast widgets, insurance, the «own page»
 * banner) and entries that opt out with `pendingSkeleton: false` (e.g. the aside copy of reviews).
 */
const collapsedWhilePending = (section: any) =>
  section.noWrapper || section.pendingSkeleton === false || COLLAPSED_WHILE_PENDING.has(section.sectionId);

export const ProfileSectionList = ({ sections, allowFallback, states, pendingKey }: Props) => {
  const entries = sections.map((section: any, position: number) => ({ section, state: states?.[position] ?? 'decided' }));
  const decided = entries.filter(
    ({ section, state }) => state === 'decided' && (Boolean(section.isShow) || (allowFallback && Boolean(section.isShowFallback))),
  );
  // In-order reveal guarantees pending sections are a suffix, so they all render below the decided ones.
  const pending = entries.filter(({ state }) => state === 'pending');
  return (
    <>
      {decided.map(({ section }, index) => renderSection(section, index))}
      {pending.length > 0 && (
        <div key={`pending-${pendingKey ?? ''}`} className="flex flex-col space-y-3">
          {pending.map(({ section }, index) => (
            <div key={index} data-section-id={section.sectionId}>
              {collapsedWhilePending(section) ? <div aria-busy="true" /> : <ProfileSectionSkeleton id={section.sectionId} />}
            </div>
          ))}
        </div>
      )}
    </>
  );
};
